import { Injectable, effect, inject, signal } from '@angular/core';
import { AppLifecycleService } from './app-lifecycle.service';

/**
 * SafeAreaService — publishes the device safe-area insets as CSS custom
 * properties so layout never depends on `env(safe-area-inset-*)` being ready at
 * paint time.
 *
 * WHY THIS EXISTS
 * On iOS, WKWebView resolves `env(safe-area-inset-bottom)` to **0** for the first
 * paint of a newly created fixed element and only reports the real value (34px on
 * an iPhone with a home indicator) a moment later. The bottom action bar is
 * `position: fixed; bottom: 0` with `padding-bottom: space-4 + env(...)`, so on
 * entering a screen it was painted with 16px of clearance instead of 50px: the
 * button landed ~19pt from the screen edge, inside the home-indicator strip, and
 * looked "too low". Any later relayout (backgrounding the app, attaching the Web
 * Inspector) healed it — which is why the bug was invisible while measuring.
 *
 * Reading the inset once from JS and keeping the **maximum seen** removes that
 * window: by the time the technician reaches a screen with a bottom bar, the
 * value is already known, so `max(env(...), var(--fsm-safe-area-bottom))` in CSS
 * resolves correctly on the very first paint.
 *
 * The maximum is sticky ON PURPOSE: the insets of a given device/orientation do
 * not legitimately shrink, and a transient 0 reported by WKWebView must never win
 * over a real 34px. Devices that genuinely have no bottom inset (home button)
 * keep 0, so nothing is hardcoded.
 */
@Injectable({ providedIn: 'root' })
export class SafeAreaService {
  private readonly lifecycle = inject(AppLifecycleService);

  /** Largest bottom inset observed so far, in CSS pixels. */
  private readonly bottomInset = signal(0);

  private initialized = false;

  constructor() {
    // Re-read when the app returns to the foreground: iOS may have changed the
    // insets (rotation, Dynamic Island state, accessibility settings) while we
    // were away.
    effect(() => {
      this.lifecycle.foregroundCount();
      if (this.initialized) this.sync();
    });
  }

  /**
   * Start publishing insets. Idempotent — called once from an app initializer.
   * Reads immediately, again after the first frames (WKWebView usually has the
   * real value by then), and on every viewport change.
   */
  initialize(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.sync();
    // The first paint often reports 0; the next frames carry the real value.
    requestAnimationFrame(() => {
      this.sync();
      requestAnimationFrame(() => this.sync());
    });
    // And once more after the webview has definitely settled.
    setTimeout(() => this.sync(), 300);

    window.addEventListener('resize', () => this.sync(), { passive: true });
    window.addEventListener('orientationchange', () => this.sync(), {
      passive: true,
    });
    window.visualViewport?.addEventListener('resize', () => this.sync(), {
      passive: true,
    });
  }

  /**
   * Measure `env(safe-area-inset-bottom)` with a throwaway probe element and
   * publish the maximum seen to `--fsm-safe-area-bottom` on <html>.
   */
  private sync(): void {
    const measured = this.measureBottomInset();
    if (measured === null) return;
    if (measured <= this.bottomInset()) return; // keep the sticky maximum
    this.bottomInset.set(measured);
    document.documentElement.style.setProperty(
      '--fsm-safe-area-bottom',
      `${measured}px`,
    );
  }

  /** Returns the bottom inset in px, or null when it cannot be measured. */
  private measureBottomInset(): number | null {
    try {
      const probe = document.createElement('div');
      probe.style.cssText =
        'position:fixed;left:0;bottom:0;width:1px;pointer-events:none;visibility:hidden;height:env(safe-area-inset-bottom,0px);';
      document.body.appendChild(probe);
      const height = probe.getBoundingClientRect().height;
      probe.remove();
      return Number.isFinite(height) ? Math.round(height) : null;
    } catch {
      // No DOM (SSR/tests) — nothing to publish.
      return null;
    }
  }
}
